import { AcordoCliente, ParcelasCartao, TipoPlanoRenegociacaoCartao } from './../../shared/models/customer-debt-renegociation';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { PerfilService } from 'src/app/perfil/perfil.service';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { CustomerDebtRenegociationData, Divida } from 'src/app/shared/models/customer-debt-renegociation';
import PerfilCliente from 'src/app/shared/models/perfil-cliente.model';
import { BaseModalService } from 'src/app/shared/services/base-modal.service';
import { ToastService } from 'src/app/shared/toast';
import { RenegociacaoCartaoService } from '../renegociacao-cartao.service';
import { LogRenegociacaoCartaoData, RenegociacaoCartaoLoggerService } from '../renegociacao-cartao-logger.service';
import { AcaoRenegociacaoCartaoLoggerEnum } from 'src/app/shared/models/renegociacao-cartao-logger-service.model';
import { parseISO } from 'date-fns';
import { ServicoCartaoIndisponivelError } from 'src/app/shared/models/servico-cartao-indisponivel.error';
import { TipoAutenticacao } from 'src/app/shared/models/tipo-autenticacao.enum';
import { InsiderPageType, InsiderTagService } from 'src/app/shared/services/insider-tag.service';

@Component({
  selector: "app-checkout-page",
  templateUrl: "./checkout-page.component.html",
  styleUrls: ["./checkout-page.component.scss"],
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
  isMobileMatches: Observable<BreakpointState> =
    this.breakpointObserver.observe("(max-width: 768px)");
  debitId: string;
  installmentNumber: number;
  subscriptions = new Subscription();
  isCasasBahia =
    location.href.indexOf("casasbahia") > 0 || location.href.indexOf("-cb") > 0;
  disabledEmail: boolean;
  disabledSms: boolean;
  userProfile: PerfilCliente | null = null;
  nomeProduto: string;
  nomeTitular: string;
  valorDivida: number;
  acordoVigente: AcordoCliente;
  acordoAvista: boolean;

  dadosRenegociacaoCartao: CustomerDebtRenegociationData;
  hasOffers: boolean;
  currentDebt: Divida;
  fromAgreementListPage: boolean;
  parcelaSelecionada: ParcelasCartao;

  constructor(
    public breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
    private autenticacaoService: AutenticacaoService,
    private loader: LoaderService,
    private toast: ToastService,
    private locationService: Location,
    private perfilService: PerfilService,
    private baseModalService: BaseModalService,
    private renegociacaoCartaoService: RenegociacaoCartaoService,
    private renegociacaoCartaoLoggerService: RenegociacaoCartaoLoggerService,
    private locationNav: Location,
    private insiderTagService: InsiderTagService
  ) { }

  ngOnInit(): void {
    this.init()
    this.locationNav.replaceState(this.router.url);
    window.addEventListener(
      "popstate",
      this.handleGoToCurrentAgreement.bind(this)
    );
    this.subscriptions.add(
      this.route.data.subscribe(data => {
        return this.fromAgreementListPage = data['fromAgreementListPage'] ?? false
      })
    );
  }

  ngOnDestroy(): void {
    this.insiderTagService.clearPageObject();

    window.removeEventListener(
      "popstate",
      this.handleGoToCurrentAgreement.bind(this)
    )
    this.subscriptions.unsubscribe();
  }

  async init() {
    this.loader.start();
    await this.fetchDebitRenegotiationData();
    this.insiderTagService.initializeCheckoutPage(InsiderPageType.CONFIRMATION, `debt-${this.acordoVigente.idAcordo.toString()}`);
    await this.buscarClientePerfil();
    this.loader.stop();
  }

  async fetchDebitRenegotiationData(): Promise<void> {
    try {
      this.loader.start();
      this.dadosRenegociacaoCartao = await this.renegociacaoCartaoService.buscarDadosRenegociacaoCartao(true);

      this.acordoVigente = await this.renegociacaoCartaoService.buscarAcordoVigente(null, true);
      this.acordoAvista = this.acordoVigente.tipoPlano === TipoPlanoRenegociacaoCartao.A_VISTA;
      this.nomeProduto = this.acordoVigente?.nomeProduto;
      this.valorDivida = this.acordoVigente?.valorAcordo;
      this.nomeTitular = this.acordoVigente?.nomeCliente;

      if (this.fromAgreementListPage) {
        this.parcelaSelecionada = this.renegociacaoCartaoService.buscarParcelaSelecionada();
      } else {
        this.parcelaSelecionada = this.acordoVigente?.parcelas[0];
        this.renegociacaoCartaoService.parcelaSelecionada = this.parcelaSelecionada;
        this.renegociacaoCartaoService.salvarParcelaSelecionada(this.parcelaSelecionada);
      }
    } catch (error) {
      if (error instanceof ServicoCartaoIndisponivelError) {
        this.toast.show({ type: 'error', title: 'Serviço temporariamente indisponível.', text: 'Por favor, tente novamente mais tarde.' })
      } else {
        this.toast.show({ type: 'error', title: 'Erro ao buscar dados de renegociação de cartão.', text: 'Tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  get debts() {
    return (
      this.dadosRenegociacaoCartao?.dividasCliente?.flatMap(
        (debt) => debt.dividas
      ) ?? []
    );
  }

  get customerData() {
    return this.dadosRenegociacaoCartao?.contato;
  }

  get numeroCartaoMascarado() {
    return this.mascararNumeroCartao(this.acordoVigente?.idCartao);
  }

  async copyBarCodeToClipboard() {
    try {
      await navigator.clipboard.writeText(this.parcelaSelecionada.linhaDigitavel);
      this.toast.show({
        type: "success",
        title: "Linha digitável copiada com sucesso!",
      });
      this.enviarLogCheckout(AcaoRenegociacaoCartaoLoggerEnum.CARTAO_BRADESCO_COPIAR_LINHA_DIGITAVEL_ACTION);
    } catch (err) {
      this.toast.show({
        type: "error",
        title: "Erro ao Copiar",
        text: "Desculpe, não conseguimos copiar o texto para a área de transferência. Por favor, tente novamente.",
      });
    }
  }

  async buscarClientePerfil() {
    try {
      const clientePerfil = await this.perfilService.getPerfil();
      this.userProfile = clientePerfil ?? null;
      if (this.userProfile?.celular) {
        this.userProfile.celular = this.userProfile?.celular?.substring(3);
      }
    } catch (error) {
      this.userProfile = null;
    }
  }

  async showNoContactDataModal() {
    return await this.baseModalService
      .showConfirmationModal({
        text: `E-mail não cadastrado. <strong>Atualize o e-mail no seu perfil.</strong>`,
        confirmBtnText: "Ir para o meu perfil",
        deniedBtnText: "Agora não",
      })
      .afterClosed()
      .toPromise();
  }

  async sendInvoicePaymentCode() {
    if (!this.userProfile?.email) {
      const confirm = await this.showNoContactDataModal();

      if (confirm) {
        this.router.navigate([`/portal/perfil`]);
      }
      return;
    }

    const confirm = await this.baseModalService
      .showConfirmationModal({
        text: `Enviaremos o código do boleto para o e-mail cadastrado: <br/> <strong>${this.userProfile.email}</strong>`,
        confirmBtnText: "Enviar",
        deniedBtnText: "Cancelar",
      })
      .afterClosed()
      .toPromise();

    if (confirm) {
      return this.enviaBoletoEmail();
    }
  }

  async enviaBoletoEmail() {
    try {
      this.loader.start();
      const responseSendBoleto = await this.renegociacaoCartaoService.enviarEmailBoleto();
      if (responseSendBoleto) {
        this.toast.show({
          type: "success",
          title: `Código do boleto enviado por e-mail com sucesso!`,
        });
      }

      this.enviarLogCheckout(AcaoRenegociacaoCartaoLoggerEnum.CARTAO_BRADESCO_ENVIAR_EMAIL_BOLETO_ACTION);
    } catch (error) {
      if (error instanceof ServicoCartaoIndisponivelError) {
        this.toast.show({ type: 'error', title: 'Serviço temporariamente indisponível.', text: 'Por favor, tente novamente mais tarde.' })
      } else {
        this.toast.show({ type: 'error', title: 'Erro ao enviar boleto por e-mail.', text: 'Por favor tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  async downloadInvoice() {
    try {
      this.loader.start();

      await this.renegociacaoCartaoService.downloadBoleto();

      this.toast.show({
        type: "success",
        title: `Download do boleto gerado com sucesso!`,
      });

      this.enviarLogCheckout(AcaoRenegociacaoCartaoLoggerEnum.CARTAO_BRADESCO_DOWNLOAD_BOLETO_ACTION);
    } catch (error) {
      if (error instanceof ServicoCartaoIndisponivelError) {
        this.toast.show({ type: 'error', title: 'Serviço temporariamente indisponível.', text: 'Por favor, tente novamente mais tarde.' })
      } else {
        this.toast.show({ type: "error", title: "Erro ao fazer download do boleto", text: "Por favor tente novamente mais tarde.", });
      }
    } finally {
      this.loader.stop();
    }
  }

  mascararNumeroCartao(idCartao: string) {
    if (!idCartao) {
      return "";
    }
    return idCartao
      .replace(/^0+/, "")
      .replace(/^(\d{4})\d{8}(\d{4})$/, "$1.xxxx.xxxx.$2");
  }

  get isAutoLogin() {
    return this.autenticacaoService.isAutoLogin;
  }

  get hideShareActions() {
    const tipoAutenticacao: TipoAutenticacao = this.autenticacaoService.obterTipoAutenticacao();
    return this.isAutoLogin || (tipoAutenticacao === TipoAutenticacao.COLABORADOR && !this.userProfile?.email);
  }

  handleCancel() {
    this.renegociacaoCartaoService.limparDadosDivida();
    this.router.navigate(["/portal/renegociacao/cartao/lista-cartoes"]);
  }

  handleGoToCurrentAgreement() {
    this.router.navigate([
      "/portal/renegociacao/cartao/lista-cartoes/acordo",
      this.currentDebt?.idDividaBradesco ? this.currentDebt.idDividaBradesco : this.acordoVigente.idDividaBradesco,
    ]);
  }

  enviarLogCheckout(action: AcaoRenegociacaoCartaoLoggerEnum) {
    const dadosDoLog: LogRenegociacaoCartaoData = {
      actionLogger: action,
      cartao: this.acordoVigente.idCartao,
      dividaId: this.acordoVigente.idDividaBradesco,
      acordoId: this.acordoVigente.idAcordo,
      nrParcela: this.parcelaSelecionada.nrParcela,
      vencimento: parseISO(this.parcelaSelecionada.dataVencParcela).toISOString(),
      valorParcela: this.parcelaSelecionada.valor,
      linhaDigitavel: this.parcelaSelecionada.linhaDigitavel,
    }

    if (action === AcaoRenegociacaoCartaoLoggerEnum.CARTAO_BRADESCO_ENVIAR_EMAIL_BOLETO_ACTION) {
      dadosDoLog.email = this.userProfile?.email ?? '';
    }

    this.renegociacaoCartaoLoggerService.enviarLogRenegociacaoCartao(dadosDoLog)
  }
}
